/**
 * Where the World Map draws a road's bend and a place's name (lane AK, plan 22).
 * Pure geometry shared by WorldMapView (which draws it) and worldMapLayout.test.ts
 * (which checks that names do not collide and roads cross as little as the
 * layout allows — lane BJ, plan 27 wave 1b).
 */
import type { WorldMapPlace } from './worldMapModel';
import { WORLD_MAP_LAND_ROAD_CONTROLS, WORLD_MAP_LAYOUT } from './worldMapLayout';

export const LABEL_GAP = 17;

type Point = Pick<WorldMapPlace, 'x' | 'y'>;

/** Gentle hand-drawn bend: the quadratic control point bowed off the straight line
 *  by a per-road amount derived from its key, so the bend is stable between renders. */
export function roadControlPoint(from: Point, to: Point, key: string): { x: number; y: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  const bow = (hash % 2 === 0 ? 1 : -1) * Math.min(26, length * 0.09);
  return { x: (from.x + to.x) / 2 + (-dy / length) * bow, y: (from.y + to.y) / 2 + (dx / length) * bow };
}

/** A sea crossing's two cubic control points (plan 28 D54). */
export type RoadVia = readonly [Point, Point] | null | undefined;

function drawingControls(from: Point, to: Point, key: string, via?: RoadVia): RoadVia {
  if (via) return via;
  const controls = WORLD_MAP_LAND_ROAD_CONTROLS[key];
  if (!controls) return;
  const [a, b] = key.split('|').map(id => WORLD_MAP_LAYOUT.places[id]);
  const same = (point: Point, placement: Point | undefined) => placement && point.x === placement.x && point.y === placement.y;
  // Portal pairs arrive in traversal order, which can oppose the sorted key.
  // Keep the same drawn curve in either direction; custom layouts keep their
  // existing default geometry when their endpoints differ from this atlas.
  if (same(from, a) && same(to, b)) return controls;
  if (same(from, b) && same(to, a)) return [controls[1], controls[0]];
  return undefined;
}

export function roadPath(from: Point, to: Point, key: string, via?: RoadVia): string {
  via = drawingControls(from, to, key, via);
  if (via) return `M ${from.x} ${from.y} C ${via[0].x} ${via[0].y} ${via[1].x} ${via[1].y} ${to.x} ${to.y}`;
  const c = roadControlPoint(from, to, key);
  return `M ${from.x} ${from.y} Q ${c.x.toFixed(1)} ${c.y.toFixed(1)} ${to.x} ${to.y}`;
}

/** Points along the drawn road (for layout checks). */
export function roadPolyline(from: Point, to: Point, key: string, steps = 20, via?: RoadVia): Array<{ x: number; y: number }> {
  via = drawingControls(from, to, key, via);
  if (via) {
    return Array.from({ length: steps + 1 }, (_, i) => {
      const t = i / steps;
      const u = 1 - t;
      const [a, b] = via;
      return {
        x: u * u * u * from.x + 3 * u * u * t * a.x + 3 * u * t * t * b.x + t * t * t * to.x,
        y: u * u * u * from.y + 3 * u * u * t * a.y + 3 * u * t * t * b.y + t * t * t * to.y,
      };
    });
  }
  const c = roadControlPoint(from, to, key);
  return Array.from({ length: steps + 1 }, (_, i) => {
    const t = i / steps;
    const u = 1 - t;
    return { x: u * u * from.x + 2 * u * t * c.x + t * t * to.x, y: u * u * from.y + 2 * u * t * c.y + t * t * to.y };
  });
}

export function labelPosition(place: Pick<WorldMapPlace, 'x' | 'y' | 'label'>): { x: number; y: number; anchor: 'start' | 'middle' | 'end' } {
  switch (place.label) {
  case 'above': return { x: place.x, y: place.y - LABEL_GAP, anchor: 'middle' };
  case 'left': return { x: place.x - LABEL_GAP, y: place.y + 5, anchor: 'end' };
  case 'right': return { x: place.x + LABEL_GAP, y: place.y + 5, anchor: 'start' };
  default: return { x: place.x, y: place.y + LABEL_GAP + 10, anchor: 'middle' };
  }
}

/** The label's box in view units, estimated from WorldMap.css (15px main-road names,
 *  13px side names; the display serif's small capitals measured ~0.65em a character
 *  in lane BJ's capture, 0.57-0.65 by letter mix), padded by the paper stroke. Tests only: the DOM
 *  measures the real text. */
export function estimatedLabelBox(place: Pick<WorldMapPlace, 'x' | 'y' | 'label' | 'name' | 'onMainRoute'>): { x0: number; y0: number; x1: number; y1: number } {
  const size = place.onMainRoute ? 15 : 13;
  const width = place.name.length * size * (place.onMainRoute ? 0.6 : 0.65);
  const at = labelPosition(place);
  const x0 = at.anchor === 'middle' ? at.x - width / 2 : at.anchor === 'end' ? at.x - width : at.x;
  // Small capitals: the cap height is ~0.75em above the baseline and nothing hangs below.
  return { x0: x0 - 3, y0: at.y - size * 0.75 - 2, x1: x0 + width + 3, y1: at.y + 1 };
}

/** A landmass caption's box (WorldMap.css: 13px italic small capitals, centred). Tests only. */
export function estimatedRegionLabelBox(label: { text: string; x: number; y: number }): { x0: number; y0: number; x1: number; y1: number } {
  const width = label.text.length * 13 * 0.62;
  return { x0: label.x - width / 2 - 3, y0: label.y - 13 * 0.75 - 2, x1: label.x + width / 2 + 3, y1: label.y + 1 };
}

/** Whether a point lies inside a wash ellipse grown by `pad`. */
export function insideWash(point: Point, wash: { cx: number; cy: number; rx: number; ry: number }, pad = 0): boolean {
  return ((point.x - wash.cx) / (wash.rx + pad)) ** 2 + ((point.y - wash.cy) / (wash.ry + pad)) ** 2 <= 1;
}
